/**
 * Wagers (docs/wagers.md, docs/roadmap-2026-10.md flag `wagerUI`): two peons each stake gold on who
 * finishes their next chore first. Pure rules and words, shared by the server (server/wagers.ts), both
 * clients' Wagers cards and the Node tests (tests/wagers.test.ts).
 *
 * A wager is offered by the challenger, accepted or declined by the challenged. On accept both stakes
 * leave both purses at once ('wager' ledger rows, negative); the first of the two to finish any
 * non-luxury task after that wins the pot (one positive 'wager' row). An offer nobody answers expires
 * after a day; an open wager nobody wins expires after a week with both stakes back ('refund' rows).
 */

/** The stakes a challenger can pick, in gold each. */
export const STAKES = [10, 20, 50] as const;
export type Stake = (typeof STAKES)[number];

export type WagerState = "offered" | "open" | "settled" | "declined" | "expired";

/** An offer nobody answers expires after a day. */
export const OFFER_TTL_MS = 24 * 3_600_000;
/** An open wager nobody wins expires after a week, both stakes back. */
export const OPEN_TTL_MS = 7 * 24 * 3_600_000;

/** How many finished wagers the card shows under the live ones. */
export const PAST_SHOWN = 3;

/** One row of the `wagers` table (migrations/0041_wagers.sql). Names are stored as displayed. */
export interface Wager {
  id: string;
  challenger: string;
  challenged: string;
  stake: number;
  state: WagerState;
  created_at: string;
  accepted_at: string | null;
  settled_at: string | null;
  winner: string | null;
  task_id: string | null;
}

/** GET /api/wagers: the caller's offered and open wagers, newest first, and the last few finished. */
export interface WagersResponse {
  live: Wager[];
  past: Wager[];
}

export function isStake(n: unknown): n is Stake {
  return typeof n === "number" && (STAKES as readonly number[]).includes(n);
}

/** The pot an accepted wager pays its winner: both stakes. */
export function potOf(stake: number): number {
  return stake * 2;
}

/** The stake chips a peon holding `balance` may pick: only the amounts they hold. */
export function stakesFor(balance: number): Stake[] {
  return STAKES.filter((s) => balance >= s);
}

/** Whether both purses hold `stake`, so the challenge can stand. */
export function canOffer(balances: { mine: number; theirs: number }, stake: number): boolean {
  return isStake(stake) && balances.mine >= stake && balances.theirs >= stake;
}

/** The refusal when one of the two cannot cover the stake: the other peon by name, or the caller. */
export function coverRefusal(name: string | null): string {
  return name ? `${name} can't cover that.` : "Not enough gold.";
}

/** Same name, ignoring case and spacing (names are matched case-insensitively everywhere). */
export function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  const k = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  return typeof a === "string" && typeof b === "string" && k(a) === k(b);
}

/** The other party of a wager from `me`'s side. */
export function opponentOf(w: Pick<Wager, "challenger" | "challenged">, me: string): string {
  return sameName(w.challenger, me) ? w.challenged : w.challenger;
}

/**
 * A wager's state at `now`: the stored state, except that an offer past a day or an open wager past
 * a week reads as expired even before the sweep has written it (and can no longer be accepted or won).
 */
export function wagerState(w: Pick<Wager, "state" | "created_at" | "accepted_at">, now: number): WagerState {
  if (w.state === "offered" && now - Date.parse(w.created_at) >= OFFER_TTL_MS) return "expired";
  if (w.state === "open" && w.accepted_at && now - Date.parse(w.accepted_at) >= OPEN_TTL_MS) return "expired";
  return w.state;
}

/** Whether `me` was challenged to this offer and can still answer it. */
export function canAnswer(w: Wager, me: string, now: number): boolean {
  return wagerState(w, now) === "offered" && sameName(w.challenged, me);
}

/** The card's head line: "Hue vs Omie · 20 gold each · first chore wins". */
export function wagerTitle(w: Pick<Wager, "challenger" | "challenged" | "stake">): string {
  return `${w.challenger} vs ${w.challenged} · ${w.stake} gold each · first chore wins`;
}

/** The short line under it, from `me`'s side: who it waits for, or how it ended. */
export function wagerStatus(w: Wager, me: string, now: number): string {
  const state = wagerState(w, now);
  const other = opponentOf(w, me);
  switch (state) {
    case "offered":
      return sameName(w.challenger, me) ? `Waiting for ${other}` : `${other} challenges you`;
    case "open":
      return "On. First chore wins.";
    case "settled":
      return sameName(w.winner, me) ? `You won ${potOf(w.stake)} gold` : `${w.winner ?? other} won ${potOf(w.stake)} gold`;
    case "declined":
      return sameName(w.challenged, me) ? "You declined" : `${other} declined`;
    case "expired":
      return w.accepted_at ? "Nobody finished. Gold back." : "Nobody answered";
  }
}

/** Names `me` may challenge now: everyone else, less anyone already in an offered or open wager with them. */
export function challengeable(names: readonly string[], me: string, live: readonly Wager[], now: number): string[] {
  const busy = live.filter((w) => {
    const s = wagerState(w, now);
    return s === "offered" || s === "open";
  });
  return names.filter((n) => !sameName(n, me) && !busy.some((w) => sameName(opponentOf(w, me), n)));
}

/** Over the stake chips once a name is picked. */
export function stakeQuestion(name: string): string {
  return `How much against ${name}?`;
}

/** Refused when the pair already has an offered or open wager. */
export const PAIR_BUSY = "You two already have a wager.";
/** Refused when the offer ran out or was already answered. */
export const TOO_LATE = "That wager is over.";

/** The pushes (docs/product.md: the title says it, the body is a few words, no numbers). */
export function challengePush(challenger: string): { title: string; body: string } {
  return { title: `${challenger} challenges you`, body: "First to finish a chore wins the pot." };
}
export function wonPush(): { title: string; body: string } {
  return { title: "You won the wager", body: "The pot is yours." };
}
export function lostPush(winner: string): { title: string; body: string } {
  return { title: "You lost the wager", body: `${winner} got there first.` };
}
